import type { OutboxState } from '@jq4race/shared';
import { type OutboxItem, UNSYNCED_OUTBOX_STATES } from '../domain/outbox-item.js';
import type { OfflineDb, OutboxRow } from './dexie/offline-db.js';

/**
 * DexieOutboxRepository — the OutboxRepository port over the Dexie `outbox`
 * table (design §5, task 6.3). Mirrors DexieBlobStore/DexieKvStore's own
 * pattern: a concrete class IS the port, no separate interface — callers
 * (enqueue.ts, and later Slice 7's SyncEngine) depend on this class
 * directly.
 *
 * Durability: `put()`/`update()` do not resolve until the underlying Dexie
 * transaction has committed, matching every other Dexie adapter in this
 * module — a caller awaiting `put()` can treat the write as durable.
 */
export class DexieOutboxRepository {
  constructor(private readonly db: OfflineDb) {}

  async put(item: OutboxItem): Promise<void> {
    await this.db.outbox.put(item as unknown as OutboxRow);
  }

  async get(id: string): Promise<OutboxItem | undefined> {
    return (await this.db.outbox.get(id)) as unknown as OutboxItem | undefined;
  }

  async byState(state: OutboxState): Promise<OutboxItem[]> {
    return (await this.db.outbox.where('state').equals(state).toArray()) as unknown as OutboxItem[];
  }

  async update(id: string, patch: Partial<OutboxItem>): Promise<void> {
    await this.db.outbox.update(id, patch);
  }

  /**
   * unsynced() — every item still needing sync or operator attention
   * (everything except terminal `synced`/`discarded`). Used by the
   * duplicate-plate local-outbox check (coordinator finding #3) — a
   * `rejected`/`held` item is still a real, unresolved duplicate risk, not
   * just `pending`/`syncing` ones.
   */
  async unsynced(): Promise<OutboxItem[]> {
    return (await this.db.outbox
      .where('state')
      .anyOf(UNSYNCED_OUTBOX_STATES)
      .toArray()) as unknown as OutboxItem[];
  }

  /**
   * Monotonic per-device ordering counter (design "seq" — FIFO replay
   * order). Not cross-tab-atomic (two tabs enqueuing in the same instant
   * could observe the same "last" row and mint the same seq) — acceptable
   * for slice 6: single-flight locking across tabs is Slice 7's own
   * concern, and a seq collision only affects display/replay ORDER, never
   * correctness (each item is still keyed by its own `id`/clientRequestId).
   */
  async nextSeq(): Promise<number> {
    const last = await this.db.outbox.orderBy('seq').last();
    return (last?.seq ?? 0) + 1;
  }
}
